import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Typography,
  InputAdornment,
  Chip,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  Search as SearchIcon,
  Folder as FolderIcon,
  Description as FileIcon,
  ArrowUpward as ArrowUpIcon,
} from '@mui/icons-material';
import patternService from '../services/pattern.service';
import patternContainerService from '../services/patternContainerService';
import { ExercisePattern, PatternContainer } from '../types';
import { toast } from 'react-toastify';

interface PatternBrowserProps {
  onSelect: (pattern: ExercisePattern) => void;
  /** Patterns already selected (will be shown as disabled) */
  excludeIds?: number[];
  /** Max height of the list area */
  maxHeight?: number;
}

const PatternBrowser: React.FC<PatternBrowserProps> = ({
  onSelect,
  excludeIds = [],
  maxHeight = 350,
}) => {
  const [patterns, setPatterns] = useState<ExercisePattern[]>([]);
  const [containers, setContainers] = useState<PatternContainer[]>([]);
  const [currentContainer, setCurrentContainer] = useState<PatternContainer | null>(null);
  const [navigationPath, setNavigationPath] = useState<PatternContainer[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [allPatterns, setAllPatterns] = useState<ExercisePattern[]>([]);
  const [allContainers, setAllContainers] = useState<PatternContainer[]>([]);
  const [dataLoaded, setDataLoaded] = useState(false);

  const loadAllData = useCallback(async () => {
    if (dataLoaded) return;
    try {
      setLoading(true);
      const [patternsData, containersData] = await Promise.all([
        patternService.getAllPatterns(),
        patternContainerService.getContainers(null)
      ]);
      setAllPatterns(Array.isArray(patternsData) ? patternsData : []);
      setAllContainers(Array.isArray(containersData) ? containersData : []);
      setDataLoaded(true);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [dataLoaded]);

  const loadItems = useCallback(async () => {
    try {
      setLoading(true);
      if (currentContainer) {
        const contents = await patternContainerService.getContainerContents(currentContainer.id);
        setContainers(Array.isArray(contents?.containers) ? contents.containers : []);
        setPatterns(Array.isArray(contents?.patterns) ? contents.patterns : []);
      } else {
        const [containerData, patternData] = await Promise.all([
          patternContainerService.getContainers(null),
          patternService.getPatterns(null)
        ]);
        const validContainerData = Array.isArray(containerData) ? containerData : [];
        const validPatternData = Array.isArray(patternData) ? patternData : [];
        setContainers(validContainerData);
        setPatterns(validPatternData.filter(p => !p.container));
      }
    } catch (error) {
      console.error('Error loading items:', error);
    } finally {
      setLoading(false);
    }
  }, [currentContainer]);

  const filterItems = useCallback(() => {
    if (!searchQuery.trim()) {
      loadItems();
      return;
    }

    const query = searchQuery.toLowerCase();

    const getPatternContainerPath = (pattern: ExercisePattern): PatternContainer[] => {
      const path: PatternContainer[] = [];
      let currentId = pattern.container;
      while (currentId) {
        // eslint-disable-next-line no-loop-func
        const container = allContainers.find(c => c.id === currentId);
        if (!container) break;
        path.unshift(container);
        currentId = container.parent;
      }
      return path;
    };

    const filteredPatterns = allPatterns.filter(pattern => {
      if (pattern.title.toLowerCase().includes(query)) return true;
      const containerPath = getPatternContainerPath(pattern);
      return containerPath.some(c => c.title.toLowerCase().includes(query));
    });

    const filteredContainers = allContainers.filter(container => {
      if (container.title.toLowerCase().includes(query)) return true;
      return allPatterns.some(pattern => {
        if (!pattern.title.toLowerCase().includes(query)) return false;
        const containerPath = getPatternContainerPath(pattern);
        return containerPath.some(c => c.id === container.id);
      });
    });

    setPatterns(filteredPatterns);
    setContainers(filteredContainers);
  }, [searchQuery, allPatterns, allContainers, loadItems]);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  useEffect(() => {
    if (!searchQuery) {
      loadItems();
    }
  }, [currentContainer, searchQuery, loadItems]);

  useEffect(() => {
    if (searchQuery) {
      filterItems();
    }
  }, [searchQuery, filterItems]);

  const handleContainerClick = (container: PatternContainer) => {
    setCurrentContainer(container);
    setNavigationPath(prev => [...prev, container]);
    setSearchQuery('');
  };

  const handleBackClick = () => {
    if (navigationPath.length > 0) {
      const newPath = [...navigationPath];
      newPath.pop();
      setNavigationPath(newPath);
      setCurrentContainer(newPath.length > 0 ? newPath[newPath.length - 1] : null);
      setSearchQuery('');
    }
  };

  const handleBreadcrumbClick = (index: number) => {
    if (index === -1) {
      setNavigationPath([]);
      setCurrentContainer(null);
    } else {
      const newPath = navigationPath.slice(0, index + 1);
      setNavigationPath(newPath);
      setCurrentContainer(newPath[index]);
    }
    setSearchQuery('');
  };

  const excludeSet = new Set(excludeIds);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <TextField
        fullWidth
        size="small"
        placeholder="Rechercher un pattern ou un dossier..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      {!searchQuery && navigationPath.length > 0 && (
        <Breadcrumbs sx={{ fontSize: '0.8rem' }}>
          <Link
            component="button"
            variant="caption"
            onClick={() => handleBreadcrumbClick(-1)}
            sx={{ cursor: 'pointer' }}
          >
            Racine
          </Link>
          {navigationPath.map((container, index) => (
            <Link
              key={container.id}
              component="button"
              variant="caption"
              onClick={() => handleBreadcrumbClick(index)}
              sx={{ cursor: 'pointer' }}
              color={index === navigationPath.length - 1 ? 'text.primary' : 'inherit'}
            >
              {container.title}
            </Link>
          ))}
        </Breadcrumbs>
      )}

      {searchQuery && (
        <Typography variant="caption" color="text.secondary">
          {patterns.length + containers.length} résultat(s)
        </Typography>
      )}

      <Box sx={{ maxHeight, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <List dense disablePadding>
            {!searchQuery && currentContainer && (
              <ListItem disablePadding>
                <ListItemButton onClick={handleBackClick} dense>
                  <ArrowUpIcon sx={{ mr: 1.5, fontSize: 18, color: 'action.active' }} />
                  <ListItemText primary=".." primaryTypographyProps={{ variant: 'body2' }} />
                </ListItemButton>
              </ListItem>
            )}

            {containers.map((container) => (
              <ListItem key={`c-${container.id}`} disablePadding>
                <ListItemButton onClick={() => handleContainerClick(container)} dense>
                  <FolderIcon sx={{ mr: 1.5, fontSize: 18, color: 'primary.main' }} />
                  <ListItemText
                    primary={container.title}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 'bold' }}
                  />
                </ListItemButton>
              </ListItem>
            ))}

            {patterns.map((pattern) => {
              const isExcluded = excludeSet.has(pattern.id);
              return (
                <ListItem key={`p-${pattern.id}`} disablePadding>
                  <ListItemButton
                    onClick={() => !isExcluded && onSelect(pattern)}
                    dense
                    disabled={isExcluded}
                  >
                    <FileIcon sx={{ mr: 1.5, fontSize: 18, color: isExcluded ? 'action.disabled' : 'action.active' }} />
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Typography variant="body2">{pattern.title}</Typography>
                          {pattern.is_reusable && (
                            <Chip label="Réitérable" size="small" color="primary" sx={{ height: 18, fontSize: '0.65rem' }} />
                          )}
                          {isExcluded && (
                            <Chip label="Déjà ajouté" size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                          )}
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}

            {!loading && patterns.length === 0 && containers.length === 0 && (
              <Box p={3} textAlign="center">
                <Typography variant="body2" color="text.secondary">
                  {searchQuery ? 'Aucun résultat trouvé' : 'Aucun pattern dans ce dossier'}
                </Typography>
              </Box>
            )}
          </List>
        )}
      </Box>
    </Box>
  );
};

export default PatternBrowser;
